import { useState } from 'react';
import { Stack } from '@mui/material';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useDebounce } from '@uidotdev/usehooks';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { RelativelyPositionedSearchFilter, Table } from '@/components';
import { DEBOUNCE_TIME_MS } from '@/constants';
import { getContentLanguage, Group, useProduct, useProducts } from '@/core';
import {
  findRowById,
  getSavedCurrentPage,
  getSavedRowsPerPage,
  getSavedSortModel,
  handleActivateItems,
  handleDeactivateItems,
  handleDeleteItems,
  languageSortComparator,
  numberSortComparator,
  onQueryError,
  onSelectAll,
  onSelectRow,
  saveCurrentPageToLocalStorage,
  saveRowsPerPageToLocalStorage,
  saveSortModelToLocalStorage,
} from '@/core/helpers';
import { DefaultCell, ImageCell, IsActiveStatusChipCell, PriceCell } from '@/core/renderCells';
import { FoodsAndProductsTableHeader, PageHeader, useDuplicateProduct } from '@/features';
import { FOODS_AND_PRODUCTS_ROUTE } from '@/routes';

const FOODS_AND_PRODUCTS_PAGE_KEY = 'foodsAndProducts';

export const PageFoodsAndProducts = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  //pagination states - initialize from local storage
  const [currentPage, setCurrentPage] = useState<number>(
    getSavedCurrentPage(FOODS_AND_PRODUCTS_PAGE_KEY),
  );
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    getSavedRowsPerPage(FOODS_AND_PRODUCTS_PAGE_KEY),
  );

  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [sortModel, setSortModel] = useState<GridSortModel>(
    getSavedSortModel(FOODS_AND_PRODUCTS_PAGE_KEY),
  );
  const debouncedSearchInputValue = useDebounce(searchInputValue, DEBOUNCE_TIME_MS);

  const columns: GridColDef[] = [
    {
      field: 'plu_code',
      headerName: 'PLU',
      renderCell: (params) => <DefaultCell value={params.value} />,
    },
    {
      field: 'image_id',
      width: 60,
      headerName: '',
      renderCell: (params) => <ImageCell imageId={params.value} />,
    },
    {
      field: 'name',
      headerName: t('HEADERS.NAME'),
      sortComparator: languageSortComparator,
      flex: 0.5,
      renderCell: (params) => (
        <DefaultCell value={params.value[getContentLanguage(i18n.language)]} />
      ),
    },
    {
      field: 'groups',
      headerName: t('HEADERS.CATEGORY'),
      flex: 0.4,
      sortable: false,
      renderCell: (params) => (
        <DefaultCell
          value={params.value
            .map((group: Group) => group.name[getContentLanguage(i18n.language)])
            .join(', ')}
        />
      ),
    },
    {
      field: 'base_price',
      headerName: t('HEADERS.BASE_PRICE'),
      sortComparator: numberSortComparator,
      flex: 0.2,
      renderCell: (params) => <PriceCell price={params.value} justifyContent="flex-start" />,
    },
    {
      field: 'final_price',
      headerName: t('HEADERS.PRICE'),
      sortComparator: numberSortComparator,
      flex: 0.2,
      renderCell: (params) => <PriceCell price={params.value} isBold justifyContent="flex-start" />,
      sortable: false,
    },
    {
      field: 'status',
      flex: 0.4,
      headerName: t('HEADERS.STATUS'),
      renderCell: (params) => <IsActiveStatusChipCell status={params.value} />,
    },
  ];

  const activeSortItem = sortModel[0];

  const {
    useProductsQuery: { data, status, refetch },
    useProductsSearch,
  } = useProducts({
    page: currentPage,
    limit: rowsPerPage,
    sortBy: activeSortItem?.field,
    sortOrder: activeSortItem?.sort ?? undefined,
  });
  const {
    mutations: {
      createMutation,
      updateMutation,
      deleteMutation,
      activateMutation,
      deactivateMutation,
    },
  } = useProduct();
  const { duplicateProduct, isPending: isDuplicating } = useDuplicateProduct();

  const { data: productsFilteredByName, status: productsFilteredByNameStatus } =
    useProductsSearch(debouncedSearchInputValue);

  const isFilterActive = debouncedSearchInputValue.length > 0;
  const displayRows = isFilterActive ? productsFilteredByName : data?.results || [];
  const displayTotal = isFilterActive
    ? productsFilteredByName?.length || 0
    : data?.total_results || 0;
  const paginationTotal = isFilterActive
    ? productsFilteredByName?.length || (debouncedSearchInputValue ? 1 : 0)
    : data?.total_results || 0;

  const handleCancelAction = () => {
    setSelectedRows([]);
  };

  const onRowEdit = (productId: number) => {
    if (!data) return;
    const selectedProduct = findRowById(data.results, productId);
    if (!selectedProduct) return;
    navigate(`${FOODS_AND_PRODUCTS_ROUTE}/${productId}`);
  };

  const onHeaderButtonClick = async () => {
    const createResponse = await createMutation.mutateAsync({
      name: { cs: 'Nový produkt', en: 'New product', de: 'Neues Produkt' },
      base_price: '0',
      status: 'hidden',
    });
    if (createResponse.status === 200 && createResponse.data) {
      navigate(`${FOODS_AND_PRODUCTS_ROUTE}/${createResponse.data.id}`);
    } else {
      onQueryError({ errorMessage: t('PRODUCT.CREATE_FAILURE') });
    }
  };

  const handleDeleteProducts = async () => {
    await handleDeleteItems({
      selectedRows,
      setSelectedRows,
      refetch,
      deleteMutation,
    });
  };

  const handleActivateProducts = async () => {
    await handleActivateItems({
      selectedRows,
      setSelectedRows,
      refetch,
      activateMutation,
    });
  };

  const handleDuplicateProduct = async () => {
    if (selectedRows.length !== 1 || !data) return;
    const originalId = selectedRows[0];
    const original = findRowById(data.results, originalId);
    if (!original) return;

    const newProductId = await duplicateProduct(originalId, original.name, {
      description: original.description,
      base_price: original.base_price,
      image_id: original.image_id,
      group_ids: original.groups.map((g) => g.id),
      allergen_ids: original.allergen_ids,
      tag_ids: original.tags.map((t) => t.id),
    });

    void refetch();
    navigate(`${FOODS_AND_PRODUCTS_ROUTE}/${newProductId}`);
  };

  const handleDeactivateProducts = async () => {
    await handleDeactivateItems({
      selectedRows,
      setSelectedRows,
      refetch,
      deactivateMutation,
    });
  };

  return (
    <Stack>
      <PageHeader onButtonClick={onHeaderButtonClick} actionButtonLabel={t('GLOBAL.ADD')} />
      <RelativelyPositionedSearchFilter
        inputValue={searchInputValue}
        setInputValue={setSearchInputValue}
        placeholder={t('PRODUCT.SEARCH_BY_NAME_PLACEHOLDER')}
      />
      <Table
        key={`${JSON.stringify(data)}${productsFilteredByName}${isFilterActive}`}
        rows={displayRows}
        tableId={FOODS_AND_PRODUCTS_PAGE_KEY}
        columns={columns}
        isDraggable={false}
        isCheckBox
        sortModel={sortModel}
        onSortModelChange={(model) => {
          setSortModel(model);
          saveSortModelToLocalStorage(FOODS_AND_PRODUCTS_PAGE_KEY, model);
        }}
        isRowsEditable
        loading={
          status === 'pending' ||
          createMutation.status === 'pending' ||
          updateMutation.status === 'pending' ||
          deleteMutation.status === 'pending' ||
          productsFilteredByNameStatus === 'pending'
        }
        onEditRow={onRowEdit}
        selectedRows={selectedRows}
        onSelectRow={(selectedRowId) => onSelectRow(selectedRowId, setSelectedRows)}
        onSelectAll={() => onSelectAll(data, selectedRows, setSelectedRows)}
        pagination
        paginationOptions={{
          currentPage: displayRows && displayRows.length > 0 ? currentPage : 0,
          rowsPerPage,
          totalRows: paginationTotal,
          rowCount: paginationTotal,
          rowsPerPageOptions: [15, 25, 50, 100, 150, 200],
          onPageSizeChange: async (newPageSize: number) => {
            setCurrentPage(0);
            setRowsPerPage(newPageSize);
            saveCurrentPageToLocalStorage(FOODS_AND_PRODUCTS_PAGE_KEY, 0);
            saveRowsPerPageToLocalStorage(FOODS_AND_PRODUCTS_PAGE_KEY, newPageSize);
          },
          onPageChange: async (newPage: number) => {
            setCurrentPage(newPage);
            saveCurrentPageToLocalStorage(FOODS_AND_PRODUCTS_PAGE_KEY, newPage);
          },
          pageSize: rowsPerPage,
          page: currentPage,
        }}
        ToolBar={(props) => (
          <FoodsAndProductsTableHeader
            {...props}
            count={displayTotal}
            selectedRows={selectedRows}
            onDelete={handleDeleteProducts}
            onDeactivate={handleDeactivateProducts}
            onActivate={handleActivateProducts}
            onCancel={handleCancelAction}
            onDuplicate={handleDuplicateProduct}
            isDuplicateDisabled={selectedRows.length !== 1 || isDuplicating}
          />
        )}
      />
    </Stack>
  );
};
